import React from "react";
import { capitalize } from "diagnostics-lib/string";
import schema_errors from "./json_errors/schema_fr";
import runtime_errors from "./json_errors/runtime_fr";
import tooltip from "./tooltip/fr";
import type { CustomTranslations } from "../../../../i18n/types";

const fr = {
  "blocks.diagram.annotations.value.system_by_event.other": (count: number) =>
    `Autre (${count} événement${count > 1 ? "s" : ""})`,
  "common.link.error.invalid_url": "URL invalide",
  "dashboard.linked_event.menu.content": ({
    linkedEventCount,
    linkedToHome,
    isPrivate,
  }: {
    linkedEventCount: number;
    linkedToHome?: React.ReactNode;
    isPrivate: boolean;
  }) => {
    const blockPrivate = isPrivate ? <>(non visible car privé)</> : <></>;
    if (!linkedToHome && linkedEventCount)
      return (
        <>
          Utilisé dans {linkedEventCount} événement{linkedEventCount > 1 ? "s" : ""} {blockPrivate}
        </>
      );
    else if (linkedToHome && !linkedEventCount)
      return (
        <>
          Utilisé dans la {linkedToHome} {blockPrivate}
        </>
      );
    else if (linkedToHome && linkedEventCount)
      return (
        <>
          Utilisé dans la {linkedToHome}
          {" et "}
          {linkedEventCount} événement
          {linkedEventCount > 1 ? "s" : ""} {blockPrivate}
        </>
      );
    return <></>;
  },
  "dashboard.linked_event.menu.content.home_page": "page d’accueil",
  "daterangepicker.error.end_gt_limit": (end: string) =>
    `La date de fin sélectionnée est après la dernière date d'analyse (${end}). Aucune donnée ne sera affichée entre la date sélectionnée et cette date.`,
  "daterangepicker.error.end_lt_start": `La date de fin ne peut pas être avant la date de début. Veuillez sélectionner une plage de dates valide`,
  "daterangepicker.error.start_gt_end":
    "La date de début ne peut pas être après la date de fin. Veuillez sélectionner une plage de dates valide.",
  "daterangepicker.error.start_lt_limit": (start: string) =>
    `La date de début sélectionnée est avant la première date d'analyse (${start}). Aucune donnée ne sera affichée entre la date sélectionnée et cette date.`,
  "project.dashboard.actions.admin_edit.hint": (translations: CustomTranslations) =>
    `Écrasez les modifications en cours sur le ${translations.FR.DASHBOARD.toLowerCase()} pour pouvoir reprendre la main et le modifier`,
  "project.dashboard.actions.block.add": "Ajouter un bloc",
  "project.dashboard.actions.block.open_json_editor": "Ouvrir l'éditeur JSON",
  "project.dashboard.actions.cancel": "Quitter l'édition",
  "project.dashboard.actions.create": (translations: CustomTranslations) =>
    `Créer un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.actions.create.error": (translations: CustomTranslations) =>
    `Une erreur est survenue lors de la création du ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.actions.create.success": (translations: CustomTranslations) =>
    `La création du ${translations.FR.DASHBOARD.toLowerCase()} a réussi.`,
  "project.dashboard.actions.delete": `Supprimer`,
  "project.dashboard.actions.delete.error": (name: string, translations: CustomTranslations) =>
    `Une erreur est survenue lors de la suppression du ${translations.FR.DASHBOARD.toLowerCase()} ${name}.`,
  "project.dashboard.actions.delete.success": (name: string, translations: CustomTranslations) =>
    `La suppression du ${translations.FR.DASHBOARD.toLowerCase()} ${name} a réussi.`,
  "project.dashboard.actions.duplicate": "Dupliquer",
  "project.dashboard.actions.duplicate.error": "Une erreur est survenue pendant la duplication.",
  "project.dashboard.actions.duplicate.success": (name: string) =>
    `La duplication de ${name} a réussi.`,
  "project.dashboard.actions.edit": "Modifier",
  "project.dashboard.actions.edit.lock.hint": (translations: CustomTranslations) =>
    `Le ${translations.FR.DASHBOARD.toLowerCase()} est en lecture seule. Seul l'auteur et les utilisateurs avec un rôle de paramétrage ou plus sur le projet peuvent modifier le ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.actions.export": "Exporter",
  "project.dashboard.actions.export.error": "Une erreur est survenue pendant l'export.",
  "project.dashboard.actions.export.error.diagram_encode":
    "Impossible d'encoder la ressource du diagramme pendant l'export.",
  "project.dashboard.actions.export.error.diagram_fetch":
    "Impossible de recuperer la ressource du diagramme pendant l'export.",
  "project.dashboard.actions.export.error.image_encode":
    "Impossible d'encoder la ressource de l'image pendant l'export.",
  "project.dashboard.actions.export.error.image_fetch":
    "Impossible de recuperer la ressource de l'image pendant l'export.",
  "project.dashboard.actions.export.json": "Format JSON",
  "project.dashboard.actions.export.partial_success": (filename: string) =>
    `L'export de ${filename} a réussi, mais certaines images n'ont pas pu être incluses dans le fichier.`,
  "project.dashboard.actions.export.pdf": "Format PDF",
  "project.dashboard.actions.export.success": (filename: string) =>
    `L'export de ${filename} a réussi.`,
  "project.dashboard.actions.import": "Importer",
  "project.dashboard.actions.insert_grid_item": (
    direction: "left" | "up" | "right" | "down",
  ): string => {
    switch (direction) {
      case "left":
        return "Ajouter un bloc à gauche";
      case "up":
        return "Ajouter un bloc en haut";
      case "right":
        return "Ajouter un bloc à droite";
      case "down":
        return "Ajouter un bloc en bas";
      default:
        return "Ajouter un bloc";
    }
  },
  "project.dashboard.actions.manage_sharing": "Gérer le partage",
  "project.dashboard.actions.manage_sharing.disabled_hint":
    "Disponible seulement pour l’auteur et les utilisateurs avec un rôle de paramétrage ou plus sur le projet.",
  "project.dashboard.actions.move_down": "Déplacer vers le bas",
  "project.dashboard.actions.move_grid_item": (
    direction: "left" | "up" | "right" | "down",
  ): string => {
    switch (direction) {
      case "left":
        return "Déplacer vers la gauche";
      case "up":
        return "Déplacer vers le haut";
      case "right":
        return "Déplacer vers la droite";
      case "down":
        return "Déplacer vers le bas";
      default:
        return "Déplacer";
    }
  },
  "project.dashboard.actions.move_up": "Déplacer vers le haut",
  "project.dashboard.actions.override": `Écraser les modifications en cours`,
  "project.dashboard.actions.publish": `Partager`,
  "project.dashboard.actions.publish.error": (name: string, translations: CustomTranslations) =>
    `Une erreur est survenue pendant le partage du ${translations.FR.DASHBOARD.toLowerCase()} ${name}.`,
  "project.dashboard.actions.publish.success": (name: string, translations: CustomTranslations) =>
    `Le ${translations.FR.DASHBOARD.toLowerCase()} ${name} a été partagé.`,
  "project.dashboard.actions.replace": "Remplacer",
  "project.dashboard.actions.save": "Enregistrer",
  "project.dashboard.actions.save.error": `Une erreur est survenue pendant l'enregistrement.`,
  "project.dashboard.actions.save.success": (
    translations: CustomTranslations,
    published?: boolean,
  ) => (
    <>
      {published ? (
        <strong>Le {translations.FR.DASHBOARD.toLowerCase()} a été mis à jour</strong>
      ) : (
        <span>Le {translations.FR.DASHBOARD.toLowerCase()} a été mis à jour</span>
      )}
      <br />
      {published ? "Les modifications sont visibles par tous" : ""}
    </>
  ),
  "project.dashboard.actions.unpublish": `Rendre privé`,
  "project.dashboard.actions.unpublish.error": (name: string, translations: CustomTranslations) =>
    `Une erreur est survenue pendant le changement du partage du ${translations.FR.DASHBOARD.toLowerCase()} ${name}.`,
  "project.dashboard.actions.unpublish.success": (name: string, translations: CustomTranslations) =>
    `Le ${translations.FR.DASHBOARD.toLowerCase()} ${name} a été rendu privé.`,
  "project.dashboard.author.anonymous": "Anonyme",
  "project.dashboard.author.prefix": "Créé par",
  "project.dashboard.block.average-losses.blank.title":
    "Aucune donnée disponible pour cette période",
  "project.dashboard.block.comment_rich_text.editable_text.updated":
    "Le contenu du bloc de texte éditable a été mis à jour.",
  "project.dashboard.block.comment_rich_text.modified_by": ({
    author,
    date,
  }: {
    author: string;
    date: string;
  }) => `Modifié par ${author} le ${date}`,
  "project.dashboard.block.comment_rich_text.placeholder": "Cliquez ici pour ajouter un texte",
  "project.dashboard.block.comment_rich_text.text.tooltip": "Modifier le texte",
  "project.dashboard.block.comment_rich_text.title": "Modifiable uniquement en mode Lecture",
  "project.dashboard.block.diagram.above_absolute_normalized_gap": (
    value: number,
    comparison: "gte" | "lte",
    absolute: boolean,
  ) =>
    `${absolute ? "Valeur absolue de l’écart normalisé" : "Valeur de l’écart normalisé"} ${
      comparison === "gte" ? "supérieure à" : "inférieure à"
    } ${value} σ`,
  "project.dashboard.block.diagram.above_absolute_threshold": (
    value: number,
    comparison: "gte" | "lte",
    unit: string,
  ) => `Valeur absolue ${comparison === "gte" ? "supérieure à" : "inférieure à"} ${value} ${unit}`,
  "project.dashboard.block.diagram.above_threshold": (value: number, unit: string) =>
    `Au-dessus du seuil de ${value} ${unit}`,
  "project.dashboard.block.diagram.below_threshold": (value: number, unit: string) =>
    `En-dessous du seuil de ${value} ${unit}`,
  "project.dashboard.block.diagram.blank.subtitle":
    "Utilisez la section à droite pour télécharger une image et ajouter des annotations par-dessus.",
  "project.dashboard.block.diagram.blank.title": "Aucun diagramme disponible à afficher",
  "project.dashboard.block.diagram.display.all":
    "Afficher par défaut les annotations détaillées en mode lecture",
  "project.dashboard.block.diagram.display.dark_mode": "Mode sombre pour les grandeurs",
  "project.dashboard.block.diagram.display.last_analysis":
    "Mettre à jour automatiquement en cas de nouvelle analyse",
  "project.dashboard.block.diagram.display.last_analysis.detail":
    "En cas de chargement d’un nouveau point plus récent, les données affichées seront automatiquement rafraichies sur cette date",
  "project.dashboard.block.diagram.display.roles": "Afficher par défaut le rôle des grandeurs",
  "project.dashboard.block.diagram.display.title": "Affichage",
  "project.dashboard.block.diagram.displayed_analysis": `Analyse affichée`,
  "project.dashboard.block.diagram.displayed_analysis.details": `La mise à jour automatique en cas de nouvelle analyse est activée. En cas de chargement d’un nouveau point plus récent, les données affichées seront automatiquement rafraichies sur cette date`,
  "project.dashboard.block.diagram.displayed_period": "Période",
  "project.dashboard.block.diagram.evolution.detail":
    "L’évolution est calculée en comparant la valeur au point précédent :",
  "project.dashboard.block.diagram.evolution.detail.computation":
    "Cette évolution est calculée sur la base des valeurs absolues pour les écarts, les écarts normalisés, écarts externes et les impacts sur les indicateurs de performance",
  "project.dashboard.block.diagram.evolution.detail.down":
    "Une flèche descendante s'affiche si l'évolution est inférieure à -20 %",
  "project.dashboard.block.diagram.evolution.detail.up":
    "Une flèche montante s'affiche si l'évolution est supérieure à 20 %",
  "project.dashboard.block.diagram.evolution.down":
    "La valeur a diminué de plus de 20 % par rapport au point précédent",
  "project.dashboard.block.diagram.evolution.down.absolute":
    "La valeur a diminué de plus de 20 % en valeur absolue par rapport au point précédent",
  "project.dashboard.block.diagram.evolution.up":
    "La valeur a augmenté de plus de 20 % par rapport au point précédent",
  "project.dashboard.block.diagram.evolution.up.absolute":
    "La valeur a augmenté de plus de 20 % en valeur absolue par rapport au point précédent",
  "project.dashboard.block.diagram.expected_unavailable": "Valeur attendue non disponible",
  "project.dashboard.block.diagram.external_impacts_unavailable": (name: string) =>
    `Impact sur ${name} non disponible`,
  "project.dashboard.block.diagram.false_detection": "Mise en fausse détection",
  "project.dashboard.block.diagram.fault_not_detected": "Défaillance non détectée",
  "project.dashboard.block.diagram.full_annotation.close": "Réduire l'annotation",
  "project.dashboard.block.diagram.full_annotation.open": "Étendre l'annotation",
  "project.dashboard.block.diagram.invalid_quantity": "Valeur invalidée",
  "project.dashboard.block.diagram.menu": "Paramètres de visualisation du diagramme",
  "project.dashboard.block.diagram.menu.details": "Afficher des annotations détaillées",
  "project.dashboard.block.diagram.menu.display_invalid": "Afficher les points invalidés",
  "project.dashboard.block.diagram.menu.display_invalid.description":
    "Si activé, la mesure invalidée est affichée",
  "project.dashboard.block.diagram.menu.display_roles": "Afficher les rôles",
  "project.dashboard.block.diagram.menu.faults": "Statut des défaillances affichés",
  "project.dashboard.block.diagram.menu.quantities": "Grandeurs",
  "project.dashboard.block.diagram.no_data": "Aucun contenu à afficher",
  "project.dashboard.block.diagram.role_label": "Rôles",
  "project.dashboard.block.diagram.std_unavailable": "Écart-type non disponible",
  "project.dashboard.block.diagram.threshold.error.absolute":
    "Les valeurs renseignées doivent être positives",
  "project.dashboard.block.diagram.threshold.error.inconsistent":
    "Les seuils choisis sont incohérents. Il n’est pas possible de définir un seuil “Supérieur ou égal à” plus petit qu’un seuil “Inférieur ou égal à”.",
  "project.dashboard.block.diagram.undefined_gap": "Écart non-calculable",
  "project.dashboard.block.diagram.undefined_normalized_gap": "Écart normalisé non-calculable",
  "project.dashboard.block.diagram.undefined_unexplained_losses": (unexplainedLosses: string) =>
    `${unexplainedLosses} non-calculable`,
  "project.dashboard.block.image.blank.subtitle":
    "Utiliser la section à droite pour importer une image",
  "project.dashboard.block.image.blank.title": "Aucune image disponible à afficher",
  "project.dashboard.block.kind": (kind: string): string =>
    kind === "TEXT"
      ? "texte"
      : kind === "HEADING"
        ? "titre"
        : kind === "TABLE"
          ? "tableau"
          : kind === "PLOT"
            ? "graphique"
            : kind === "IMAGE"
              ? "image"
              : kind === "DIAGRAM"
                ? "Diagramme"
                : "",
  "project.dashboard.block.loading.text":
    "Le contenu est en train de se charger, cela peut prendre quelques instants.",

  "project.dashboard.block.loading.title": "Chargement en cours",
  "project.dashboard.block.modal.addCustom": "Paramétrable",
  "project.dashboard.block.modal.addPreset": "Prédéfini",
  "project.dashboard.block.period": (from: string, to: string, duration: string) =>
    `Données chargées du ${from} au ${to} (${duration})`,
  "project.dashboard.block.plot.axis_hint": "Double-cliquez pour afficher toutes les données",
  "project.dashboard.block.plot.empty_series": (names: string[]) =>
    names.length > 1 ? (
      <>
        Les séries <strong>{names.join(", ")}</strong> ne contiennent aucune information à
        présenter.
      </>
    ) : (
      <>
        La série <strong>{names}</strong> ne contient aucune information à présenter.
      </>
    ),
  "project.dashboard.block.plot.error.config.description":
    "Veuillez vérifier et modifier la configuration pour permettre la création du graphe.",
  "project.dashboard.block.plot.error.config.title":
    "La configuration des séries et des graphes est incorrecte.",
  "project.dashboard.block.plot.error.data.description":
    "Veuillez ajouter les données via le panneau situé à droite.",
  "project.dashboard.block.plot.error.data.title": "Données non sélectionnées",
  "project.dashboard.block.plot.na_category": "non renseigné",
  "project.dashboard.block.production-monitoring.blank.title":
    "Aucune donnée disponible pour cette période",
  "project.dashboard.block.rich_text.inline_editor.equation.title":
    "Double-cliquez pour éditer l’équation.",
  "project.dashboard.block.rich_text.modal.equation.input_label": "Équation (en LaTeX)",
  "project.dashboard.block.rich_text.modal.equation.preview_label": "Prévisualisation",
  "project.dashboard.block.rich_text.modal.equation.preview_label.empty":
    "Aucune équation à visualiser",
  "project.dashboard.block.rich_text.modal.equation.subtitle": "Insérer une équation en LaTeX",
  "project.dashboard.block.rich_text.modal.equation.tooltip.text.description":
    "Des exemples de syntaxe LaTeX sont disponibles dans le guide utilisateur.",
  "project.dashboard.block.rich_text.modal.equation.tooltip.text.link":
    "Lien vers la section dédiée",
  "project.dashboard.block.rich_text.modal.insert": "Insérer",
  "project.dashboard.block.rich_text.modal.link.error": "URL invalide",
  "project.dashboard.block.rich_text.modal.link.label": "Lien",
  "project.dashboard.block.rich_text.modal.link.placeholder":
    "Entrez l'URL (commençant par https://)",
  "project.dashboard.block.rich_text.toolbar.bold": "Gras",
  "project.dashboard.block.rich_text.toolbar.equation.insert": "Insérer une équation",
  "project.dashboard.block.rich_text.toolbar.italic": "Italique",
  "project.dashboard.block.rich_text.toolbar.link.error.invalid_url": "URL invalide",
  "project.dashboard.block.rich_text.toolbar.link.insert": "Insérer un lien",
  "project.dashboard.block.rich_text.toolbar.link.remove": "Supprimer le lien",
  "project.dashboard.block.rich_text.toolbar.link.replace": "Remplacer le lien",
  "project.dashboard.block.sub_subtitle.placeholder": "Sous-sous-titre (3000 caractères maximum)",
  "project.dashboard.block.subtitle.placeholder": "Sous-titre (3000 caractères maximum)",
  "project.dashboard.block.template.anomaly_detection_last_week_alerts.title":
    "Alertes sur la dernière semaine",
  "project.dashboard.block.template.average-losses-preset.title": "Pertes moyennes",
  "project.dashboard.block.template.category.advanced": "Avancé",
  "project.dashboard.block.template.category.plot": "Graphique",
  "project.dashboard.block.template.category.preset": "Prédéfini",
  "project.dashboard.block.template.category.text": "Texte",
  "project.dashboard.block.template.category.visual": "Visuel",
  "project.dashboard.block.template.comment.title": "Texte éditable",
  "project.dashboard.block.template.costImpact.title": "Estimation de l'impact financier",
  "project.dashboard.block.template.diagram.sub_subtitle": "Diagramme",
  "project.dashboard.block.template.empty.description":
    "Bloc au paramétrage avancé et plus flexible",
  "project.dashboard.block.template.empty.title": "JSON",
  "project.dashboard.block.template.heading.sub_subtitle": "Sous-sous-titre",
  "project.dashboard.block.template.heading.subtitle": "Sous-titre",
  "project.dashboard.block.template.heading.title": "Titre",
  "project.dashboard.block.template.image.sub_subtitle": "Image",
  "project.dashboard.block.template.image.subtitle": "Image",
  "project.dashboard.block.template.image.title": "Image",
  "project.dashboard.block.template.priority-fault-events-preset.title": "Événements prioritaires",
  "project.dashboard.block.template.production-monitoring-preset.title": "Suivi de la production",
  "project.dashboard.block.template.scatterplot.description":
    "Tracé d'un élément en fonction d'un autre (graphe XY)",
  "project.dashboard.block.template.scatterplot.title": "Nuage de points",
  "project.dashboard.block.template.table.description": "Affichage de données tabulaires",
  "project.dashboard.block.template.table.title": "Tableau",
  "project.dashboard.block.template.text.title": "Texte",
  "project.dashboard.block.template.timeseries.description": "Analyse en fonction du temps",
  "project.dashboard.block.template.timeseries.title": "Évolution temporelle",
  "project.dashboard.block.text.placeholder": "Ajouter un texte (3000 caractères maximum)",
  "project.dashboard.block.title.placeholder": "Titre (3000 caractères maximum)",
  "project.dashboard.block.zoom.reset.title": "Réinitialiser le zoom",
  "project.dashboard.delete.message.linked_events": ({
    linkedEventCount,
    isLinkedToHome,
    translations,
  }: {
    linkedEventCount: number;
    isLinkedToHome: boolean;
    translations: CustomTranslations;
  }) => {
    let sentence = <></>;
    if (!isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          <strong>
            {linkedEventCount === 1 ? "l'événement" : `les ${linkedEventCount} événements`}
          </strong>{" "}
          {linkedEventCount === 1 ? "qui l'utilise" : "qui l'utilisent"}.
        </>
      );
    else if (isLinkedToHome && !linkedEventCount) sentence = <strong>la page d’accueil.</strong>;
    else if (isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          <strong>
            la page d’accueil et{" "}
            {linkedEventCount === 1 ? "l'événement" : `les ${linkedEventCount} événements`}
          </strong>{" "}
          qui l&apos;utilisent.
        </>
      );

    return (
      <>
        Suite à cette action, le {translations.FR.DASHBOARD.toLowerCase()} ne sera plus visible dans{" "}
        {sentence}
      </>
    );
  },
  "project.dashboard.description.placeholder": (translations: CustomTranslations) =>
    `Ajoutez une description au ${translations.FR.DASHBOARD.toLowerCase()} (3000 caractères maximum)`,
  "project.dashboard.editor.blank_state.subtitle":
    "Sélectionnez un bloc pour accéder à ses paramètres",
  "project.dashboard.editor.blank_state.title": "Aucun bloc n’est sélectionné",
  "project.dashboard.editor.copy.error":
    "Une erreur est survenue pendant la copie du contenu JSON.",
  "project.dashboard.editor.copy.success":
    "Le contenu JSON du bloc a été copié dans le presse-papier.",
  "project.dashboard.editor.gui.position": "Position du bloc :",
  "project.dashboard.editor.gui.section.aggregation_range.title": "Période d’agrégation",
  "project.dashboard.editor.gui.section.annotation_settings.absolute_threshold": "Valeur absolue",
  "project.dashboard.editor.gui.section.annotation_settings.action": "Ajouter",
  "project.dashboard.editor.gui.section.annotation_settings.area": "Rectangle avec libellé",
  "project.dashboard.editor.gui.section.annotation_settings.assigned_threshold": "Attribué à",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_absolute_threshold":
    "Option uniquement disponible pour les écarts",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_amplitude":
    "Uniquement disponible pour les défaillances détectées par le Metroscope",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_expected_gap":
    "Disponible uniquement pour les grandeurs utilisées comme sortie du modèle",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_external_impacts":
    "Disponible uniquement pour les grandeurs utilisées comme écart externe",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_normalized_gap":
    "Disponible uniquement pour les grandeurs utilisées comme observable",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_std":
    "Disponible uniquement pour les grandeurs mesurées",
  "project.dashboard.editor.gui.section.annotation_settings.dot": "Point avec libellé",
  "project.dashboard.editor.gui.section.annotation_settings.label_only": "Libellé seul",
  "project.dashboard.editor.gui.section.annotation_settings.system_filter":
    "Défaillances incluses dans le calcul",
  "project.dashboard.editor.gui.section.annotation_settings.system_value":
    "Afficher l'impact sur les indicateurs de performance :",
  "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.description":
    (performanceIndicator: string) => (
      <>
        La répartition est affichée uniquement pour la puissance produite{" "}
        <strong>{performanceIndicator}</strong>.
      </>
    ),
  "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.toggle":
    "Répartition par événement",
  "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.tooltip.disabled":
    "Uniquement disponible pour la puissance produite",
  "project.dashboard.editor.gui.section.annotation_settings.threshold_placeholder": "Valeur",
  "project.dashboard.editor.gui.section.annotation_settings.title": "Paramètres de l’annotation :",
  "project.dashboard.editor.gui.section.annotation_settings.value": "Valeurs affichées :",
  "project.dashboard.editor.gui.section.data.add_button": "Ajouter",

  "project.dashboard.editor.gui.section.data.title": "Données",
  "project.dashboard.editor.gui.section.display.cycle.title": "Afficher les intercycles",
  "project.dashboard.editor.gui.section.display.invalidated_data_points":
    "Afficher les points invalidés",
  "project.dashboard.editor.gui.section.display.marker_info.empty":
    "Sélectionner le type de marqueur",
  "project.dashboard.editor.gui.section.display.marker_info.title": "Information du marqueur",
  "project.dashboard.editor.gui.section.display.publication.title":
    "Afficher les marqueurs de publication",
  "project.dashboard.editor.gui.section.display.sync.title": "Synchroniser le zoom",
  "project.dashboard.editor.gui.section.display.temporal_zoom.title": "Plage affichée par défaut",
  "project.dashboard.editor.gui.section.display.title": "Marqueurs de graphe",
  "project.dashboard.editor.gui.section.displayed_value.analysis": "Analyse",
  "project.dashboard.editor.gui.section.displayed_value.description": () => (
    <>
      <strong>Analyse</strong> : La valeur affichée correspond à celle sur l’analyse sélectionnée.
      <br />
      <strong>Période</strong> : La valeur affichée sera la moyenne pondérée des valeurs observées
      sur la période d’agrégation (en fonction de la durée entre 2 points) lorsque la valeur est une
      puissance. Sinon, les valeurs affichées seront les valeurs médiane, minimum et maximum
      observées sur la période d’agrégation.
    </>
  ),
  "project.dashboard.editor.gui.section.displayed_value.period": "Période",
  "project.dashboard.editor.gui.section.displayed_value.title": "Valeur affichée",
  "project.dashboard.editor.gui.section.encoding.title": (n?: number) =>
    typeof n === "number" ? `Axe ${n + 1}` : "Axe",
  "project.dashboard.editor.gui.section.encoding.zoom.max.label": "Max",
  "project.dashboard.editor.gui.section.encoding.zoom.max.placeholder": "Max",
  "project.dashboard.editor.gui.section.encoding.zoom.min.label": "Min",
  "project.dashboard.editor.gui.section.encoding.zoom.min.placeholder": "Min",
  "project.dashboard.editor.gui.section.encoding.zoom.title": "Échelle de l’axe",
  "project.dashboard.editor.gui.section.evolution.title": "Évolution",
  "project.dashboard.editor.gui.section.group_by_unit.title": "Grouper les axes par unité",
  "project.dashboard.editor.gui.section.period.title": "Plage temporelle",
  "project.dashboard.editor.gui.section.rule.max.label": "Max",
  "project.dashboard.editor.gui.section.rule.min.label": "Min",
  "project.dashboard.editor.gui.section.rule.plot_bands":
    "Afficher les zones de dépassement des seuils",
  "project.dashboard.editor.gui.section.rule.title": "Seuil",
  "project.dashboard.editor.gui.section.time_range.title": "Plage chargée",
  "project.dashboard.editor.gui.section.title.annotation": (count: number) =>
    `Annotation${count > 1 ? "s" : ""} (${count})`,
  "project.dashboard.editor.gui.section.title.content": "Contenu",
  "project.dashboard.editor.gui.section.title.diagram": "Titre du diagramme",
  "project.dashboard.editor.gui.section.title.image": "Titre",
  "project.dashboard.editor.gui.section.title.label": "Libellé",
  "project.dashboard.editor.gui.section.title.plot": "Titre du graphique",
  "project.dashboard.editor.gui.section.title.title_image": "Titre de l'image",
  "project.dashboard.editor.gui.title": (kind: string) => {
    const lowerKind = kind.toLowerCase();
    return lowerKind === "image" ? `Paramètres de l’${lowerKind}` : `Paramètres du ${lowerKind}`;
  },
  "project.dashboard.editor.image.file.action": "Importer",

  "project.dashboard.editor.image.file.label": "Image",
  "project.dashboard.editor.image.title.label": "Titre de l'image",
  "project.dashboard.editor.json.title": (kind: string) =>
    kind.toLowerCase() === "image"
      ? `Paramètres de l’${kind} (JSON)`
      : `Paramètres du ${kind} (JSON)`,
  "project.dashboard.editor.no_editor.subtitle": "Seul le contenu peut être édité",

  "project.dashboard.editor.no_editor.title": "Aucun paramétrage n’est disponible",
  "project.dashboard.encoding.kind": (kind: string, manual?: boolean): string =>
    kind === "QUANTITY"
      ? "Grandeur"
      : kind === "FAULT"
        ? manual
          ? "Défaillance non-modélisée"
          : "Défaillance"
        : kind === "SYSTEM"
          ? "Système"
          : "",
  "project.dashboard.encoding.unknown": "Non-identifié",
  "project.dashboard.fault.non_modelled": "Non modélisée",
  "project.dashboard.fault.not_detected": "Non détectée",
  "project.dashboard.fault.status.confirmed": "Confirmée",
  "project.dashboard.fault.status.false_detection": "Fausse détection",
  "project.dashboard.fault.status.suspected": "Suspectée",
  "project.dashboard.fault.status.to_analyse": "À analyser",
  "project.dashboard.found.error": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.DASHBOARD)} introuvable.`,
  "project.dashboard.header.modal.footer.duplicate_button": `Dupliquer`,
  "project.dashboard.header.modal.footer.edit_button": `Modifier`,
  "project.dashboard.header.modal.form.changed_dashboard": (translations: CustomTranslations) =>
    `Modifier le ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.header.modal.form.changed_dashboard.content": (
    {
      dashboardName,
      linkedEventCount,
      isLinkedToHome,
    }: {
      dashboardName: string;
      linkedEventCount: number;
      isLinkedToHome: boolean;
    },
    translations: CustomTranslations,
  ) => {
    let sentence = <></>;
    if (!isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          Les modifications seront visibles dans{" "}
          <strong>
            {linkedEventCount > 1 ? `les ${linkedEventCount} événements` : "l'événement"}
          </strong>{" "}
          {linkedEventCount > 1 ? `qui l’utilisent.` : "qui l’utilise."}
        </>
      );
    else if (isLinkedToHome && !linkedEventCount)
      sentence = <>Les modifications seront visibles dans la page d’accueil.</>;
    else if (isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          Les modifications seront visibles dans la page d’accueil et{" "}
          <strong>
            {linkedEventCount > 1 ? `les ${linkedEventCount} événements` : "l'événement"}
          </strong>{" "}
          qui l&apos;utilisent.
        </>
      );

    return (
      <>
        Êtes-vous sûr de vouloir modifier le {translations.FR.DASHBOARD.toLowerCase()}{" "}
        <strong>{dashboardName}</strong> ? {sentence}
        <br />
        <br />
        Vous pouvez également dupliquer ce {translations.FR.DASHBOARD.toLowerCase()} pour préserver
        l&apos;original avant de poursuivre vos modifications.
      </>
    );
  },
  "project.dashboard.lite.header.button": (translations: CustomTranslations) =>
    `Accéder au ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.period.prefix": "Plage temporelle",
  "project.dashboard.placeholder.or": "ou",
  "project.dashboard.placeholder.text": (translations: CustomTranslations) =>
    `importez un ${translations.FR.DASHBOARD.toLowerCase()} depuis un fichier JSON`,
  "project.dashboard.plot.tooltip.faultStatus": "Status :",
  "project.dashboard.plot.tooltip.validity": "Validité :",
  "project.dashboard.print.date.title": "Date de l’export :",
  "project.dashboard.print.export_by.title": "Par :",
  "project.dashboard.resource.column": (column: string) => {
    if (column === "measured") return "Valeur mesurée";
    if (column === "simulated") return "Valeur simulée";
    if (column === "std") return "Écart-type";
    if (column === "validity") return "Validité de la mesure";
    if (column === "offsetExpected") return "Offset";
    if (column === "expected") return "Valeur attendue";
    if (column === "gap") return "Écart";
    if (column === "normalizedGap") return "Écart normalisé";
    if (column === "reparability") return "Réparabilité";
    if (column === "status") return "Statut";
    if (column === "faultStatus") return "Statut de défaillance";
    if (column === "real") return "Amplitude";
    if (column === "confidence") return "Niveau de confiance";
    if (column === "globalImpact") return "Impact global";
    if (column === "quantityImpacts" || column === "systemImpact" || column === "noSystemImpacts")
      return `Impact sur`;
    if (column === "externalImpacts") return `Impact externe sur`;
    if (column === "group") return `Cycle`;
    if (column === "date") return `Date`;
    if (column === "hour") return `Heure de la journée`;
    if (column === "dayOfYear") return `Jour de l’année`;
    return capitalize(column);
  },
  "project.dashboard.resource.column.annotation": (column: string) => {
    if (column === "measured") return "Mesuré";
    if (column === "simulated") return "Simulé";
    if (column === "std") return "Écart-type";
    if (column === "validity") return "Validité de la mesure";
    if (column === "offsetExpected") return "Offset";
    if (column === "expected") return "Attendu";
    if (column === "gap") return "Écart";
    if (column === "normalizedGap") return "Écart normalisé";
    if (column === "reparability") return "Réparabilité";
    if (column === "status") return "Statut";
    if (column === "faultStatus") return "Statut de défaillance";
    if (column === "real") return "Amplitude";
    if (column === "confidence") return "Niveau de confiance";
    if (column === "globalImpact") return "Impact global";
    if (column === "quantityImpacts" || column === "systemImpact" || column === "noSystemImpacts")
      return ``;
    if (column === "externalImpacts") return `Impact externe sur`;
    if (column === "group") return `Cycle`;
    if (column === "date") return `Date`;
    if (column === "hour") return `Heure de la journée`;
    if (column === "dayOfYear") return `Jour de l’année`;
    return capitalize(column);
  },
  "project.dashboard.resource.column.annotation.label.systemImpact": (
    translations: CustomTranslations,
    quantity: string,
    hasMultiplePIs: boolean,
  ) =>
    hasMultiplePIs
      ? `${capitalize(translations.FR.LOSSES)} (${quantity}) : `
      : `${capitalize(translations.FR.LOSSES)} : `,
  "project.dashboard.resource.open_quantities": "Accéder à la liste des grandeurs",
  "project.dashboard.role.label": (
    role:
      | "MODEL_INPUT"
      | "OBSERVABLE"
      | "INACTIVE_OBSERVABLE"
      | "PERFORMANCE_INDICATOR"
      | "FAULT"
      | "SYSTEM"
      | "UNEXPLAINED_LOSSES"
      | "QUANTITY"
      | "OTHER_QUANTITY",
    translations: CustomTranslations,
  ): string => {
    switch (role) {
      case "MODEL_INPUT":
        return "Entrée du modèle";
      case "OBSERVABLE":
        return "Observable";
      case "INACTIVE_OBSERVABLE":
        return "Observable désactivée";
      case "PERFORMANCE_INDICATOR":
        return "Indicateur de performance";
      case "FAULT":
        return "Défaillance";
      case "SYSTEM":
        return "Système";
      case "QUANTITY":
        return "Grandeur";
      case "UNEXPLAINED_LOSSES":
        return `${capitalize(translations.FR.LOSSES)} ${translations.FR.UNEXPLAINED.toLowerCase()}`;
      case "OTHER_QUANTITY":
        return "Autre grandeur";
      default:
        throw new Error(role satisfies never);
    }
  },
  "project.dashboard.state.editing": (name: string | null) =>
    name ? `Édition bloquée tant qu'il est en cours d'édition par ${name}` : "En cours d'édition",
  "project.dashboard.state.unsaved": `Non enregistré`,
  "project.dashboard.status.published": "Partagé",
  "project.dashboard.status.readonly": "Lecture seule",
  "project.dashboard.title.empty": "Sans titre",
  "project.dashboard.title.placeholder": (translations: CustomTranslations) =>
    `Ajouter un titre au ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.transaction.admin_take_over.action": `Quitter`,
  "project.dashboard.transaction.admin_take_over.text": (translations: CustomTranslations) =>
    `Un administrateur a pris le contrôle de ce ${translations.FR.DASHBOARD.toLowerCase()}. Toutes les modifications non enregistrées ont été perdues. Vous allez être redirigé vers le ${translations.FR.DASHBOARD.toLowerCase()} en mode lecture.`,
  "project.dashboard.transaction.admin_take_over.title": (translations: CustomTranslations) =>
    `Édition du ${translations.FR.DASHBOARD.toLowerCase()} temporairement bloquée `,
  "project.dashboard.transaction.delete.submit_action": `Supprimer`,
  "project.dashboard.transaction.delete.subtitle": (translations: CustomTranslations) =>
    `Voulez-vous supprimer le ${translations.FR.DASHBOARD.toLowerCase()} :`,
  "project.dashboard.transaction.delete.title": (translations: CustomTranslations) =>
    `Supprimer le ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.transaction.discard.cancel_action": `Continuer l'édition`,
  "project.dashboard.transaction.discard.submit_action": `Annuler`,
  "project.dashboard.transaction.discard.subtitle": (translations: CustomTranslations) =>
    `Souhaitez-vous annuler les modifications du ${translations.FR.DASHBOARD.toLowerCase()} :`,
  "project.dashboard.transaction.discard.title": `Annuler les modifications`,
  "project.dashboard.transaction.duplicate.error.conflict": `Le nom doit être différent`,
  "project.dashboard.transaction.duplicate.error.empty": `Le nom ne peut être vide`,
  "project.dashboard.transaction.duplicate.name.label": `Nom`,
  "project.dashboard.transaction.duplicate.submit_action": `Dupliquer`,
  "project.dashboard.transaction.duplicate.title": (translations: CustomTranslations) =>
    `Dupliquer le ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.transaction.import_image.error.file_too_large":
    "La taille du fichier dépasse la limite autorisée de 20 Mo.",
  "project.dashboard.transaction.import_image.error.invalid_file_type":
    "Le type de fichier n'est pas supporté. Veuillez importer un fichier au format .jpeg, .png ou .svg.",
  "project.dashboard.transaction.import_image.error.no_file":
    "Une erreur s'est produite lors du téléchargement de l'image. Veuillez réessayer.",
  "project.dashboard.transaction.import_image.error.to_many_files":
    "Vous ne pouvez importer qu'une seule image.",
  "project.dashboard.transaction.import_image.error.upload_error":
    "Une erreur s'est produite pendant le téléchargement du fichier. Veuillez essayer ulterieurement.",
  "project.dashboard.transaction.import_image.title": "Importer une image",
  "project.dashboard.transaction.manage_sharing.access.check.readonly": "Lecture seule",
  "project.dashboard.transaction.manage_sharing.access.check.readwrite": "Édition",
  "project.dashboard.transaction.manage_sharing.access.hint.readonly": (
    translations: CustomTranslations,
  ) =>
    `Seul l'auteur et les utilisateurs avec un rôle de paramétrage ou plus sur le projet peuvent modifier le ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.access.hint.readwrite": (
    translations: CustomTranslations,
  ) =>
    `Toutes les personnes ayant un rôle d'édition ou plus sur le projet peuvent modifier le ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.access.title": `Verrouillage`,
  "project.dashboard.transaction.manage_sharing.error": (translations: CustomTranslations) =>
    `Une erreur est survenue lors du partage du ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.private_to_share.message.linked_events": ({
    linkedEventCount,
    isLinkedToHome,
    translations,
  }: {
    linkedEventCount: number;
    isLinkedToHome: boolean;
    translations: CustomTranslations;
  }) => {
    let sentence = <></>;
    if (linkedEventCount && !isLinkedToHome)
      sentence = (
        <>
          <strong>
            {linkedEventCount === 1 ? (
              <>l&apos;événement</>
            ) : (
              <>les {linkedEventCount} événements</>
            )}
          </strong>{" "}
          {linkedEventCount === 1 ? "qui l'utilise" : "qui l'utilisent"}.
        </>
      );
    else if (!linkedEventCount && isLinkedToHome) sentence = <strong>la page d’accueil.</strong>;
    else if (linkedEventCount && isLinkedToHome)
      sentence = (
        <>
          <strong>
            la page d’accueil et{" "}
            {linkedEventCount === 1 ? (
              <>l&apos;événement</>
            ) : (
              <>les {linkedEventCount} événements</>
            )}
          </strong>{" "}
          qui l&apos;utilisent.
        </>
      );

    return (
      <>
        Suite à cette action, le {translations.FR.DASHBOARD.toLowerCase()} sera visible dans{" "}
        {sentence}
      </>
    );
  },
  "project.dashboard.transaction.manage_sharing.share_to_private.message.linked_events": ({
    linkedEventCount,
    isLinkedToHome,
    translations,
  }: {
    linkedEventCount: number;
    isLinkedToHome: boolean;
    translations: CustomTranslations;
  }) => {
    let sentence = <></>;
    if (linkedEventCount && !isLinkedToHome)
      sentence = (
        <>
          <strong>
            {linkedEventCount === 1 ? (
              <>l&apos;événement</>
            ) : (
              <>les {linkedEventCount} événements</>
            )}
          </strong>{" "}
          {linkedEventCount === 1 ? "qui l'utilise" : "qui l'utilisent"}.
        </>
      );
    else if (!linkedEventCount && isLinkedToHome) sentence = <strong>la page d’accueil.</strong>;
    else if (linkedEventCount && isLinkedToHome)
      sentence = (
        <>
          <strong>
            la page d’accueil et{" "}
            {linkedEventCount === 1 ? (
              <>l&apos;événement</>
            ) : (
              <>les {linkedEventCount} événements</>
            )}
          </strong>{" "}
          qui l&apos;utilisent.
        </>
      );

    return (
      <>
        Suite à cette action, le {translations.FR.DASHBOARD.toLowerCase()} ne sera plus visible dans{" "}
        {sentence}
      </>
    );
  },
  "project.dashboard.transaction.manage_sharing.success": (translations: CustomTranslations) =>
    `Le partage du ${translations.FR.DASHBOARD.toLowerCase()} a été mis à jour`,
  "project.dashboard.transaction.manage_sharing.validate": `Sauvegarder`,
  "project.dashboard.transaction.manage_sharing.visibility.check.private": "Privé",
  "project.dashboard.transaction.manage_sharing.visibility.check.shared": "Partagé",
  "project.dashboard.transaction.manage_sharing.visibility.hint.private": (
    translations: CustomTranslations,
  ) =>
    `Vous êtes la seule personne à pouvoir consulter et modifier le ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.visibility.hint.shared": (
    translations: CustomTranslations,
  ) =>
    `Le ${translations.FR.DASHBOARD.toLowerCase()} est visible par toutes les personnes ayant accès au projet.`,
  "project.dashboard.transaction.manage_sharing.visibility.title": `Visibilité`,
  "project.dashboard.transaction.publish.submit_action": `Partager`,
  "project.dashboard.transaction.publish.text_1": (translations: CustomTranslations) =>
    `Le ${translations.FR.DASHBOARD.toLowerCase()} :`,
  "project.dashboard.transaction.publish.text_2": `sera accessible par toutes les personnes ayant accès à ce projet.`,
  "project.dashboard.transaction.publish.title": (translations: CustomTranslations) =>
    `Partager le ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboard.transaction.save.cancel_action": `Annuler les modifications`,
  "project.dashboard.transaction.save.submit_action": `Enregistrer et quitter`,
  "project.dashboard.transaction.save.subtitle": (translations: CustomTranslations) =>
    `Souhaitez-vous enregistrer les modifications du ${translations.FR.DASHBOARD.toLowerCase()} avant de quitter ?`,
  "project.dashboard.transaction.save.title": `Vous avez des modifications non enregistrées`,
  "project.dashboard.transaction.unlock.action": `Continuer`,
  "project.dashboard.transaction.unlock.hint": (translations: CustomTranslations) =>
    `Débloquez le ${translations.FR.DASHBOARD.toLowerCase()} depuis la page contenant la liste des ${translations.FR.DASHBOARDS.toLowerCase()} pour pouvoir le modifier.`,
  "project.dashboard.transaction.unlock.text": (translations: CustomTranslations) =>
    `Souhaitez-vous reprendre la main sur ce ${translations.FR.DASHBOARD.toLowerCase()} et écraser les modifications ?`,
  "project.dashboard.transaction.unlock.title": `Écraser les modifications`,
  "project.dashboard.transaction.unlock.warning": `Attention, les modifications en cours seront définitivement perdues`,
  "project.dashboard.transaction.unpublish.submit_action": `Rendre privé`,
  "project.dashboard.transaction.unpublish.text_2": `ne sera visible et éditable que par son créateur.`,
  "project.dashboard.transaction.unpublish.title": (translations: CustomTranslations) =>
    `Rendre le ${translations.FR.DASHBOARD.toLowerCase()} privé`,
  "project.dashboards.empty.subtitle": (translations: CustomTranslations) =>
    `Les ${translations.FR.DASHBOARDS.toLowerCase()} personnalisés permettent de mieux comprendre et exploiter les données`,
  "project.dashboards.empty.title": (translations: CustomTranslations) =>
    `Aucun ${translations.FR.DASHBOARD.toLowerCase()} n'est défini`,
  "project.dashboards.import.action.validation": `Importer`,
  "project.dashboards.import.button.text": (translations: CustomTranslations) =>
    `Importer un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboards.import.errors.drop": "Une erreur est survenue lors du dépôt du fichier",
  "project.dashboards.import.fail": (translations: CustomTranslations) =>
    `Une erreur est survenue lors de l'import du ${translations.FR.DASHBOARD.toLowerCase()}.`,
  "project.dashboards.import.identifier_mapping_title": "Ressources à remplacer",
  "project.dashboards.import.missing_identifier":
    "Le fichier contient des ressources non identifiées. Veuillez renseigner les valeurs souhaitées pour ces ressources.",
  "project.dashboards.import.modal.subtitle": (translations: CustomTranslations) =>
    `Votre ${translations.FR.DASHBOARD.toLowerCase()} sera créé sur la base du fichier importé.`,
  "project.dashboards.import.modal.title": (translations: CustomTranslations) =>
    `Importer un ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboards.import.options.publish": (translations: CustomTranslations) =>
    `Partager le ${translations.FR.DASHBOARD.toLowerCase()}`,
  "project.dashboards.import.options.replace": (translations: CustomTranslations) =>
    `Remplacer le ${translations.FR.DASHBOARD.toLowerCase()} actuel`,
  "project.dashboards.import.select_identifier": "Sélectionner",
  "project.dashboards.import.success": (translations: CustomTranslations) =>
    `Import du ${translations.FR.DASHBOARD.toLowerCase()} réussi.`,
  "project.dashboards.import.unknown_identifier_kind": (identifiers: string[]) =>
    "Le fichier contient des ressources dont le type ne peut pas être identifié (" +
    identifiers.join(", ") +
    "). Certains graphiques devront être configurés a posteriori.",
  "project.dashboards.import.warning.summary": "Le dashboard a été importé sans elles.",
  "project.dashboards.import.warning.title": "Certaines images n'ont pas été importées",
  "project.dashboards.listing.published.empty": (translations: CustomTranslations) =>
    `Aucun ${translations.FR.DASHBOARD.toLowerCase()} partagé`,
  "project.dashboards.listing.published.title": "Partagés",
  "project.dashboards.listing.unpublished.empty": (translations: CustomTranslations) =>
    `Aucun ${translations.FR.DASHBOARD.toLowerCase()} privé`,
  "project.dashboards.listing.unpublished.title": "Privés",
  "project.dashboards.navigation": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.DASHBOARDS)}`,
  "project.dashboards.no_analyses.subtitle": (translations: CustomTranslations) =>
    `Veuillez créer au moins une analyse pour accéder et utiliser les ${translations.FR.DASHBOARDS.toLowerCase()}`,
  "project.dashboards.no_analyses.title": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.DASHBOARD)} indisponible sans analyse dans le projet`,
  "project.dashboards.table.column.actions": "Actions",
  "project.dashboards.table.column.author": "Créé par",
  "project.dashboards.table.column.createdAt": "Création",
  "project.dashboards.table.column.description": "Description",
  "project.dashboards.table.column.editor": "Modifié par",
  "project.dashboards.table.column.published": "Statut",
  "project.dashboards.table.column.title": "Nom",
  "project.dashboards.table.column.updatedAt": "Dernière modification",
  "project.dashboards.table.search.placeholder": "Rechercher",
  "project.dashboards.table.title.link.fault_event": (
    nblinks: number,
    translations: CustomTranslations,
  ) =>
    `${capitalize(translations.FR.DASHBOARD)} utilisé dans ${nblinks} événement${nblinks > 1 ? "s" : ""}`,
  "project.dashboards.table.title.link.summary": (translations: CustomTranslations) =>
    `${capitalize(translations.FR.DASHBOARD)} utilisé dans la page d’accueil`,
  ...schema_errors,
  ...runtime_errors,
  ...tooltip,
  "project.dashboard.actions.print.error": "Une erreur est survenue pendant l'export PDF.",
  "project.dashboard.actions.print.error_diagram_mismatch":
    "Le nombre d'images de diagrammes à imprimer ne correspond pas au nombre de diagrammes dans le tableau de bord.",
  "project.dashboard.actions.print.error_generating_image":
    "Échec de la génération de l'image pour le diagramme.",
  "project.dashboard.actions.print.error_processing_diagrams":
    "Échec du traitement des diagrammes pour l'impression.",
  "project.dashboard.actions.print.success": "Le PDF a été exporté avec succès.",
};

export default fr;
